
import React, {
  useEffect,
  useState,
  useCallback,
} from "react";

import {
  listProjects,
  createProject,
  listDocuments,
  uploadProjectDocuments,
  extractDocumentAI,
} from "../../services/documentApi";

const STYLES = {
  panel: {
    height: "100%",
    width: "100%",
    overflow: "auto",
    padding: 18,
    background: "#ffffff",
    border: "1px solid #e2e8f0",
    borderRadius: 12,
    boxSizing: "border-box",
    fontFamily: "inherit",
    fontSize: 12,
    color: "#0f172a",
  },
  input: {
    minHeight: 36,
    padding: "7px 10px",
    border: "1px solid #cbd5e1",
    borderRadius: 8,
    background: "#ffffff",
    color: "#0f172a",
    fontSize: 12,
    boxSizing: "border-box",
    width: "100%",
  },
  label: {
    fontSize: 11,
    fontWeight: 700,
    color: "#475569",
  },
  field: {
    display: "flex",
    flexDirection: "column",
    gap: 6,
    minWidth: 160,
    flex: "1 1 180px",
  },
  button: {
    minHeight: 36,
    padding: "8px 14px",
    border: 0,
    borderRadius: 8,
    background: "#2563eb",
    color: "#ffffff",
    fontWeight: 700,
    fontSize: 12,
    cursor: "pointer",
    whiteSpace: "nowrap",
  },
  tableCell: {
    padding: "10px 12px",
    fontSize: 12,
    borderBottom: "1px solid #e2e8f0",
    textAlign: "left",
    whiteSpace: "nowrap",
  },
};

const ACCEPTED =
  ".xlsx,.xls,.xlsm,.csv,.tsv,.pdf,.docx,.png,.jpg,.jpeg,.tiff";

function getProjectId(project) {
  return project?.id ?? project?.project_id ?? "";
}

function getProjectName(project) {
  return (
    project?.name ??
    project?.project_name ??
    String(getProjectId(project))
  );
}

function getDocumentId(document) {
  return document?.id ?? document?.document_id ?? "";
}

function getDocumentName(document) {
  return (
    document?.filename ??
    document?.document_name ??
    document?.name ??
    "Unnamed Document"
  );
}

function ActionButton({
  children,
  onClick,
  disabled = false,
  type = "button",
  color = "#2563eb",
}) {
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      style={{
        ...STYLES.button,
        background: color,
        opacity: disabled ? 0.55 : 1,
        cursor: disabled ? "not-allowed" : "pointer",
      }}
    >
      {children}
    </button>
  );
}

export default function DocumentUploadPanel({
  onViewInventory,
}) {
  const [projects, setProjects] = useState([]);
  const [projectId, setProjectId] = useState("");
  const [documents, setDocuments] = useState([]);

  const [showCreateForm, setShowCreateForm] =
    useState(false);
  const [newName, setNewName] = useState("");
  const [newDescription, setNewDescription] =
    useState("");

  const [files, setFiles] = useState([]);
  const [fileInputKey, setFileInputKey] =
    useState(0);

  const [source, setSource] = useState("plan");
  const [documentType, setDocumentType] =
    useState("");
  const [vendorId, setVendorId] =
    useState("");

  const [loadingProjects, setLoadingProjects] =
    useState(false);
  const [creatingProject, setCreatingProject] =
    useState(false);
  const [loadingDocuments, setLoadingDocuments] =
    useState(false);
  const [uploading, setUploading] =
    useState(false);
  const [extractingId, setExtractingId] =
    useState("");
  const [viewing, setViewing] =
    useState(false);

  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // ----------------------------------------
  // PROJECT LIST
  // ----------------------------------------

  const refreshProjects = useCallback(async () => {
    setLoadingProjects(true);

    try {
      const result = await listProjects();
      setProjects(result);
      return result;
    } catch (err) {
      setError(
        `Unable to load projects: ${err.message}`
      );
      return null;
    } finally {
      setLoadingProjects(false);
    }
  }, []);

  useEffect(() => {
    refreshProjects();
  }, [refreshProjects]);

  // ----------------------------------------
  // DOCUMENT LIST
  // ----------------------------------------

  const refreshDocuments = useCallback(async () => {
    if (!projectId) {
      setError("Select a project first.");
      return false;
    }

    setLoadingDocuments(true);
    setError("");

    try {
      const result = await listDocuments(projectId);
      setDocuments(result);
      return true;
    } catch (err) {
      setError(
        `Unable to refresh documents: ${err.message}`
      );
      return false;
    } finally {
      setLoadingDocuments(false);
    }
  }, [projectId]);

  useEffect(() => {
    let active = true;

    if (!projectId) {
      setDocuments([]);
      return;
    }

    setDocuments([]);
    setLoadingDocuments(true);

    listDocuments(projectId)
      .then((result) => {
        if (active) setDocuments(result);
      })
      .catch((err) => {
        if (active) {
          setError(
            `Unable to load documents: ${err.message}`
          );
        }
      })
      .finally(() => {
        if (active) {
          setLoadingDocuments(false);
        }
      });

    return () => {
      active = false;
    };
  }, [projectId]);

  // ----------------------------------------
  // CREATE PROJECT
  // ----------------------------------------

  async function handleCreateProject(event) {
    event.preventDefault();

    if (creatingProject) return;

    const name = newName.trim();

    if (!name) {
      setError("Enter the project name.");
      return;
    }

    setCreatingProject(true);
    setError("");
    setSuccess("");

    let createdProject = null;

    try {
      createdProject = await createProject({
        name,
        description: newDescription.trim(),
      });
    } catch (err) {
      setError(
        `Unable to create project: ${err.message}`
      );
      setCreatingProject(false);
      return;
    }

    // Creation succeeded. List refresh errors
    // must not be reported as creation failures.
    const newId = String(
      getProjectId(createdProject)
    );

    setProjects((current) => [
      ...current.filter(
        (p) => String(getProjectId(p)) !== newId
      ),
      createdProject,
    ]);

    setProjectId(newId);

    setNewName("");
    setNewDescription("");
    setShowCreateForm(false);
    setSuccess(
      `Project "${getProjectName(createdProject)}" created successfully.`
    );

    // Refresh the dropdown without blocking creation.
    const refreshed = await refreshProjects();

    if (!refreshed) {
      setSuccess(
        "Project created and selected. Project-list refresh was unsuccessful."
      );
    }

    setCreatingProject(false);
  }

  // ----------------------------------------
  // UPLOAD DOCUMENTS
  // ----------------------------------------

  async function handleUpload(event) {
    event.preventDefault();

    if (uploading) return;

    setError("");
    setSuccess("");

    if (!projectId || files.length === 0) {
      setError(
        "Select a project and at least one file."
      );
      return;
    }

    if (source === "vendor" && !vendorId.trim()) {
      setError("Vendor ID is required.");
      return;
    }

    setUploading(true);

    let uploadSucceeded = false;

    try {
      await uploadProjectDocuments({
        projectId,
        files,
        documentType,
        source,
        vendorId,
      });

      uploadSucceeded = true;

      setSuccess(
        "Documents uploaded successfully."
      );

      setFiles([]);
      setFileInputKey((n) => n + 1);
    } catch (err) {
      setError(
        `Upload failed: ${err.message}`
      );
    } finally {
      setUploading(false);
    }

    if (uploadSucceeded) {
      const refreshed = await refreshDocuments();

      if (!refreshed) {
        setSuccess(
          "Upload succeeded. The document list could not be refreshed."
        );
      }
    }
  }

  // ----------------------------------------
  // AI EXTRACTION
  // ----------------------------------------

  async function handleExtract(documentId) {
    if (!documentId || extractingId) return;

    setError("");
    setSuccess("");
    setExtractingId(documentId);

    try {
      const result = await extractDocumentAI(
        documentId
      );

      const status = result?.status ?? "submitted";

      setSuccess(
        `AI extraction request: ${status}.`
      );

      await refreshDocuments();
    } catch (err) {
      setError(
        `AI extraction failed: ${err.message}`
      );
    } finally {
      setExtractingId("");
    }
  }

  // ----------------------------------------
  // VIEW INVENTORY
  // ----------------------------------------

  async function handleViewInventory(filters) {
    setError("");
    setSuccess("");

    if (typeof onViewInventory !== "function") {
      setError(
        "Inventory callback is not configured."
      );
      return;
    }

    setViewing(true);

    try {
      await onViewInventory(filters);
    } catch (err) {
      setError(
        `Unable to display inventory: ${err.message}`
      );
    } finally {
      setViewing(false);
    }
  }

  const isBusy =
    uploading || creatingProject || viewing;

  return (
    <div style={STYLES.panel}>
      {/* Page header */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 12,
          flexWrap: "wrap",
          marginBottom: 16,
        }}
      >
        <div>
          <h2
            style={{
              margin: "0 0 5px",
              fontSize: 17,
              fontWeight: 800,
            }}
          >
            Project Document Management
          </h2>

          <span style={{ color: "#64748b" }}>
            Create projects, upload documents,
            extract records and view inventory.
          </span>
        </div>

        <ActionButton
          color="#0f766e"
          disabled={!projectId || isBusy}
          onClick={() =>
            handleViewInventory({ projectId })
          }
        >
          {viewing
            ? "Loading Inventory..."
            : "View Project Inventory"}
        </ActionButton>
      </div>

      {/* Project selector and create action */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          flexWrap: "wrap",
          marginBottom: 14,
        }}
      >
        <select
          style={{
            ...STYLES.input,
            width: 270,
          }}
          value={projectId}
          disabled={isBusy || loadingProjects}
          onChange={(e) => {
            setProjectId(e.target.value);
            setDocuments([]);
            setError("");
            setSuccess("");
          }}
        >
          <option value="">
            {loadingProjects
              ? "Loading projects..."
              : "Select existing project"}
          </option>

          {projects.map((project, index) => {
            const id = getProjectId(project);

            return (
              <option
                key={id || index}
                value={id}
              >
                {getProjectName(project)}
              </option>
            );
          })}
        </select>

        <ActionButton
          color="#0f766e"
          disabled={isBusy}
          onClick={() => {
            setShowCreateForm((value) => !value);
            setError("");
          }}
        >
          {showCreateForm
            ? "Cancel"
            : "+ Create Project"}
        </ActionButton>

        <ActionButton
          color="#475569"
          disabled={loadingProjects || isBusy}
          onClick={refreshProjects}
        >
          {loadingProjects
            ? "Refreshing..."
            : "Refresh Projects"}
        </ActionButton>
      </div>

      {/* Create new project form */}
      {showCreateForm && (
        <form
          onSubmit={handleCreateProject}
          style={{
            border: "1px solid #dbe3ef",
            borderRadius: 10,
            background: "#f8fafc",
            padding: 16,
            marginBottom: 18,
            maxWidth: 600,
          }}
        >
          <h3
            style={{
              fontSize: 15,
              margin: "0 0 14px",
            }}
          >
            Create New Project
          </h3>

          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 12,
            }}
          >
            <div style={STYLES.field}>
              <label
                htmlFor="new-project-name"
                style={STYLES.label}
              >
                Project Name *
              </label>
              <input
                id="new-project-name"
                style={STYLES.input}
                value={newName}
                onChange={(e) =>
                  setNewName(e.target.value)
                }
                placeholder="Enter project name"
                required
                disabled={creatingProject}
              />
            </div>

            <div style={STYLES.field}>
              <label
                htmlFor="new-project-description"
                style={STYLES.label}
              >
                Description
              </label>
              <textarea
                id="new-project-description"
                rows={3}
                style={{
                  ...STYLES.input,
                  resize: "vertical",
                  height: "auto",
                  fontFamily: "inherit",
                }}
                value={newDescription}
                onChange={(e) =>
                  setNewDescription(e.target.value)
                }
                placeholder="Project description"
                disabled={creatingProject}
              />
            </div>

            <div
              style={{
                display: "flex",
                gap: 10,
              }}
            >
              <ActionButton
                type="submit"
                disabled={
                  creatingProject || !newName.trim()
                }
              >
                {creatingProject
                  ? "Creating..."
                  : "Create Project"}
              </ActionButton>

              <ActionButton
                color="#64748b"
                disabled={creatingProject}
                onClick={() =>
                  setShowCreateForm(false)
                }
              >
                Cancel
              </ActionButton>
            </div>
          </div>
        </form>
      )}

      {/* Upload form */}
      <form
        onSubmit={handleUpload}
        style={{
          display: "flex",
          gap: 10,
          flexWrap: "wrap",
          alignItems: "flex-end",
          marginBottom: 16,
          padding: 14,
          background: "#f8fafc",
          border: "1px solid #e2e8f0",
          borderRadius: 10,
        }}
      >
        <div style={STYLES.field}>
          <label style={STYLES.label}>
            Document Source
          </label>
          <select
            style={STYLES.input}
            value={source}
            onChange={(e) =>
              setSource(e.target.value)
            }
          >
            <option value="plan">
              Plan / Expected
            </option>
            <option value="vendor">
              Vendor / Delivered
            </option>
          </select>
        </div>

        <div style={STYLES.field}>
          <label style={STYLES.label}>
            Document Type
          </label>
          <input
            style={STYLES.input}
            value={documentType}
            onChange={(e) =>
              setDocumentType(e.target.value)
            }
            placeholder="Auto detect if blank"
          />
        </div>

        {source === "vendor" && (
          <div style={STYLES.field}>
            <label style={STYLES.label}>
              Vendor ID *
            </label>
            <input
              style={STYLES.input}
              value={vendorId}
              onChange={(e) =>
                setVendorId(e.target.value)
              }
              required
              placeholder="Vendor ID"
            />
          </div>
        )}

        <div
          style={{
            ...STYLES.field,
            flex: "2 1 240px",
          }}
        >
          <label style={STYLES.label}>
            Select Documents
          </label>
          <input
            key={fileInputKey}
            type="file"
            accept={ACCEPTED}
            multiple
            onChange={(e) =>
              setFiles(
                Array.from(e.target.files || [])
              )
            }
            style={{
              ...STYLES.input,
              height: "auto",
            }}
          />
        </div>

        <ActionButton
          type="submit"
          disabled={
            !projectId ||
            files.length === 0 ||
            isBusy
          }
        >
          {uploading
            ? "Uploading..."
            : "Upload Documents"}
        </ActionButton>

        <ActionButton
          color="#475569"
          disabled={
            !projectId ||
            loadingDocuments ||
            isBusy
          }
          onClick={refreshDocuments}
        >
          {loadingDocuments
            ? "Refreshing..."
            : "Refresh Documents"}
        </ActionButton>
      </form>

      {/* Status messages */}
      {error && (
        <div
          role="alert"
          style={{
            padding: 12,
            marginBottom: 12,
            background: "#fef2f2",
            color: "#b91c1c",
            borderRadius: 8,
            overflowWrap: "anywhere",
          }}
        >
          {error}
        </div>
      )}

      {success && (
        <div
          role="status"
          style={{
            padding: 12,
            marginBottom: 12,
            background: "#f0fdf4",
            color: "#166534",
            borderRadius: 8,
          }}
        >
          {success}
        </div>
      )}

      {/* Uploaded document table */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 10,
          marginBottom: 10,
          flexWrap: "wrap",
        }}
      >
        <h3
          style={{
            margin: 0,
            fontSize: 15,
          }}
        >
          Uploaded Documents
        </h3>

        <span style={{ color: "#64748b" }}>
          {documents.length} document(s)
        </span>
      </div>

      <div
        style={{
          border: "1px solid #e2e8f0",
          borderRadius: 10,
          overflowX: "auto",
        }}
      >
        <table
          style={{
            width: "100%",
            minWidth: 900,
            borderCollapse: "collapse",
          }}
        >
          <thead>
            <tr>
              {[
                "Document ID",
                "File Name",
                "Type",
                "Source",
                "Status",
                "Records",
                "Actions",
              ].map((title) => (
                <th
                  key={title}
                  style={{
                    ...STYLES.tableCell,
                    background: "#f1f5f9",
                    fontWeight: 800,
                  }}
                >
                  {title}
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {documents.map((doc, index) => {
              const documentId =
                getDocumentId(doc);

              const values = [
                documentId,
                getDocumentName(doc),
                doc.document_type,
                doc.source,
                doc.status,
                doc.records_extracted ??
                  doc.record_count,
              ];

              return (
                <tr key={documentId || index}>
                  {values.map((value, cellIndex) => (
                    <td
                      key={cellIndex}
                      style={STYLES.tableCell}
                    >
                      {String(value ?? "—")}
                    </td>
                  ))}

                  <td style={STYLES.tableCell}>
                    <div
                      style={{
                        display: "flex",
                        gap: 8,
                      }}
                    >
                      <ActionButton
                        disabled={
                          !documentId ||
                          Boolean(extractingId)
                        }
                        onClick={() =>
                          handleExtract(documentId)
                        }
                      >
                        {extractingId === documentId
                          ? "Extracting..."
                          : "Extract AI"}
                      </ActionButton>

                      <ActionButton
                        color="#0f766e"
                        disabled={
                          !documentId || viewing
                        }
                        onClick={() =>
                          handleViewInventory({
                            projectId,
                            documentId,
                          })
                        }
                      >
                        View Inventory
                      </ActionButton>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        {loadingDocuments && (
          <p
            style={{
              textAlign: "center",
              padding: 16,
              color: "#64748b",
            }}
          >
            Loading documents...
          </p>
        )}

        {!loadingDocuments &&
          documents.length === 0 && (
            <p
              style={{
                textAlign: "center",
                padding: 24,
                color: "#64748b",
              }}
            >
              {projectId
                ? "No documents found for this project."
                : "Select or create a project to begin."}
            </p>
          )}
      </div>
    </div>
  );
}
